import { Component, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SwaggerAuthService } from '../../services/auth.service';

/**
 * Swagger Login Component
 * 
 * Component providing login functionality for Swagger UI access.
 * Uses Angular 20+ signals and modern control flow syntax.
 * 
 * Features:
 * - Reactive form with validation
 * - Loading states using signals
 * - Error handling with user feedback
 * - Return URL support for deep linking
 * - Responsive design
 * 
 * @example
 * ```typescript
 * // In routes
 * { path: 'ui/login', component: SwaggerLoginComponent }
 * ```
 */
@Component({
    selector: 'swagger-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule]
})
export class SwaggerLoginComponent implements OnInit {
    private readonly fb: FormBuilder;
    private readonly authService: SwaggerAuthService;
    private readonly router: Router;
    private readonly route: ActivatedRoute;

    // Signals for reactive state management (Angular 20+)
    readonly isLoading = signal(false);
    readonly errorMessage = signal<string | null>(null);
    readonly showPassword = signal(false);

    // Configuration properties
    logoUrl: string = '';
    platformName: string = 'Swagger Documentation';
    subtitle: string = 'Please sign in to access the API documentation';
    companyName: string = '';

    loginForm!: FormGroup;

    constructor(
        fb: FormBuilder,
        authService: SwaggerAuthService,
        router: Router,
        route: ActivatedRoute
    ) {
        this.fb = fb;
        this.authService = authService;
        this.router = router;
        this.route = route;
    }

    ngOnInit(): void {
        // Load branding settings from configuration
        this.loadBrandingSettings();

        // Initialize login form with validators
        this.loginForm = this.fb.group({
            tenancyName: [''],
            username: ['', [Validators.required, Validators.minLength(3)]],
            password: ['', [Validators.required, Validators.minLength(8)]]
        });

        // Auto-redirect if already authenticated
        if (this.authService.isAuthenticated()) {
            this.redirectAfterLogin();
        }
    }

    /**
     * Handle form submission
     */
    async onSubmit(): Promise<void> {
        // Clear previous errors
        this.errorMessage.set(null);

        // Validate form
        if (this.loginForm.invalid) {
            this.markFormGroupTouched(this.loginForm);
            return;
        }

        const { username, password } = this.loginForm.value;

        // Set loading state
        this.isLoading.set(true);

        try {
            // Attempt login
            await this.authService.login(username, password);

            // Success - redirect to Swagger or return URL
            this.redirectAfterLogin();
        } catch (error: any) {
            // Handle login error
            const errorMsg = error?.message || 'Login failed. Please try again.';
            this.errorMessage.set(errorMsg);

            // Reset password field
            this.loginForm.patchValue({ password: '' });
        } finally {
            // Clear loading state
            this.isLoading.set(false);
        }
    }

    /**
     * Toggle password visibility
     */
    togglePasswordVisibility(): void {
        this.showPassword.update(current => !current);
    }

    /**
     * Check if form field has error
     */
    hasFieldError(fieldName: string): boolean {
        const field = this.loginForm.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    /**
     * Check if form field is invalid and touched
     */
    isFieldInvalid(fieldName: string): boolean {
        const field = this.loginForm.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    /**
     * Get current year for footer
     */
    getCurrentYear(): number {
        return new Date().getFullYear();
    }

    /**
     * Get error message for a specific field
     */
    getFieldError(fieldName: string): string | null {
        const field = this.loginForm.get(fieldName);

        if (!field || !field.errors || !field.touched) {
            return null;
        }

        if (field.errors['required']) {
            return `${this.capitalizeFirst(fieldName)} is required`;
        }

        if (field.errors['minlength']) {
            const minLength = field.errors['minlength'].requiredLength;
            return `${this.capitalizeFirst(fieldName)} must be at least ${minLength} characters`;
        }

        return 'Invalid input';
    }

    /**
     * Load branding settings from configuration
     */
    private loadBrandingSettings(): void {
        const settings = this.authService.getSettings();
        if (settings.branding) {
            this.platformName = settings.branding.platformName || 'Platform';
            this.subtitle = settings.branding.subtitle || 'Administrator Access Required';
            this.companyName = settings.branding.companyName || this.platformName;
            this.logoUrl = settings.branding.logoUrl || '';
        }
    }

    /**
     * Redirect after successful login
     */
    private redirectAfterLogin(): void {
        // Get encrypted token from auth service
        const encryptedToken = this.authService.getToken();

        // Redirect to dashboard with encrypted token in router state
        this.router.navigate(['/ui/dashboard'], {
            state: { encryptedToken }
        });
    }

    /**
     * Mark all fields in form group as touched (for validation display)
     */
    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            control?.markAsTouched();

            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            }
        });
    }

    /**
     * Capitalize first letter of string
     */
    private capitalizeFirst(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }
}
